import React, { useState } from 'react';
import { CloudArrowUpIcon, DocumentIcon, PhotoIcon, TrashIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Card, Button, FileUpload, Badge } from '../../components/Common';
import { uploadAPI } from '../../services/api';

const UploadManager = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  const handleFileSelect = (file) => {
    if (file) {
      const newFile = {
        id: Date.now(),
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        status: 'pending',
        progress: 0,
      };
      setFiles(prev => [...prev, newFile]);
    }
  };

  const handleUpload = async (fileItem) => {
    setUploading(true);
    setUploadProgress(prev => ({ ...prev, [fileItem.id]: 0 }));

    try {
      const formData = new FormData();
      formData.append('file', fileItem.file);

      await uploadAPI.upload(formData);

      setFiles(prev => prev.map(f => f.id === fileItem.id ? { ...f, status: 'completed', progress: 100 } : f));
      setUploadProgress(prev => ({ ...prev, [fileItem.id]: 100 }));
    } catch (error) {
      setFiles(prev => prev.map(f => f.id === fileItem.id ? { ...f, status: 'error' } : f));
    } finally {
      setUploading(false);
    }
  };

  const handleUploadAll = async () => {
    const pendingFiles = files.filter(f => f.status === 'pending');
    for (const file of pendingFiles) {
      await handleUpload(file);
    }
  };

  const handleRemove = (id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return PhotoIcon;
    return DocumentIcon;
  };

  const pendingCount = files.filter(f => f.status === 'pending').length;
  const completedCount = files.filter(f => f.status === 'completed').length;

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Upload Manager</h1>
          <p className="text-slate-500">Upload and manage your files</p>
        </div>
        {pendingCount > 0 && (
          <Button icon={CloudArrowUpIcon} onClick={handleUploadAll} loading={uploading}>
            Upload All ({pendingCount})
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
              <CloudArrowUpIcon className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Files</p>
              <p className="text-2xl font-bold text-slate-800">{files.length}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <DocumentIcon className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Pending</p>
              <p className="text-2xl font-bold text-slate-800">{pendingCount}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <CheckCircleIcon className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Completed</p>
              <p className="text-2xl font-bold text-slate-800">{completedCount}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Upload Area */}
      <Card title="Upload Files" subtitle="Drag and drop or click to select files">
        <FileUpload label="" accept="*/*" maxSize={10} onChange={handleFileSelect} />
      </Card>

      {/* File List */}
      {files.length > 0 && (
        <Card title="Upload Queue" subtitle={`${files.length} files in queue`} noPadding>
          <div className="divide-y divide-slate-100">
            {files.map((fileItem) => {
              const Icon = getFileIcon(fileItem.type);
              return (
                <div key={fileItem.id} className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{fileItem.name}</p>
                      <p className="text-sm text-slate-500">{formatFileSize(fileItem.size)}</p>
                      {fileItem.status === 'pending' && uploadProgress[fileItem.id] > 0 && (
                        <div className="mt-2 progress">
                          <div className="progress-bar" style={{ width: `${uploadProgress[fileItem.id]}%` }} />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={fileItem.status === 'completed' ? 'success' : fileItem.status === 'error' ? 'danger' : 'warning'}>
                      {fileItem.status}
                    </Badge>
                    {fileItem.status === 'pending' && (
                      <Button size="sm" onClick={() => handleUpload(fileItem)} loading={uploading}>Upload</Button>
                    )}
                    <button onClick={() => handleRemove(fileItem.id)} className="p-2 hover:bg-red-50 rounded-lg">
                      <TrashIcon className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
};

export default UploadManager;
